import { type Day } from '@choco-admin/types';
import { Button, Card, CardBody, Chip } from '@heroui/react';
import React, { useState } from 'react';
import { FiClock, FiEdit2, FiInfo } from 'react-icons/fi';

import { useSupplierDeliverySetting } from '../../../../../hooks/useSupplierDeliverySetting';

import { DeliverySettingsModal } from './DeliverySettingsModal';

interface DeliverySettingsSectionProps {
  chatId: string;
}

const DAYS: Day[] = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

const CUTOFF_TYPE_LABELS: Record<string, string> = {
  no_cutoff: 'No cutoff',
  strict: 'Strict cutoff',
  flexible: 'Flexible cutoff',
};

interface DeliverySettingsView {
  deliveryDays: Record<Day, boolean>;
  hasCutoffByDay: Record<Day, boolean>;
  cutoffTimes: Record<Day, string>;
  cutoffDays: Record<Day, string>;
  cutoffType: string;
}

// Company defaults shown when the chat has no custom delivery settings:
// every day available with no cutoff.
const DEFAULT_SETTINGS: DeliverySettingsView = {
  deliveryDays: {
    Monday: true,
    Tuesday: true,
    Wednesday: true,
    Thursday: true,
    Friday: true,
    Saturday: true,
    Sunday: true,
  },
  hasCutoffByDay: {
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
    Sunday: false,
  },
  cutoffTimes: {
    Monday: '23:59:59',
    Tuesday: '23:59:59',
    Wednesday: '23:59:59',
    Thursday: '23:59:59',
    Friday: '23:59:59',
    Saturday: '23:59:59',
    Sunday: '23:59:59',
  },
  cutoffDays: {
    Monday: '1',
    Tuesday: '1',
    Wednesday: '1',
    Thursday: '1',
    Friday: '1',
    Saturday: '1',
    Sunday: '1',
  },
  cutoffType: 'no_cutoff',
};

/** "23:59:59" -> "23:59" for display */
const formatTime = (time: string): string => time?.slice(0, 5) || '23:59';

/** Lead time in days -> human label, mirroring the modal's day selector */
const formatLeadTime = (leadTime: string): string => {
  const days = parseInt(leadTime, 10);
  if (Number.isNaN(days) || days === 0) return 'Same day';
  return `${days} day${days === 1 ? '' : 's'} before`;
};

export const DeliverySettingsSection: React.FC<
  DeliverySettingsSectionProps
> = ({ chatId }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mappedSettings } = useSupplierDeliverySetting({ id: chatId });

  const isUsingDefaults = !mappedSettings;
  const settings: DeliverySettingsView = mappedSettings ?? DEFAULT_SETTINGS;

  // Cutoff type is aggregated per-day by the backend and collapses to
  // 'no_cutoff' when delivery days carry different cutoff settings. Surface that
  // as "Mixed" rather than mislabelling days that do have a cutoff.
  const hasAnyCutoff = DAYS.some(
    (day) => settings.deliveryDays[day] && settings.hasCutoffByDay[day]
  );
  const cutoffTypeLabel =
    settings.cutoffType === 'no_cutoff' && hasAnyCutoff
      ? 'Mixed'
      : (CUTOFF_TYPE_LABELS[settings.cutoffType] ?? settings.cutoffType);

  const renderCutoffCell = (day: Day) => {
    if (!settings.deliveryDays[day]) {
      return <span className="text-default-400">No delivery</span>;
    }
    if (settings.hasCutoffByDay[day]) {
      return (
        <span className="text-default-600">
          {formatLeadTime(settings.cutoffDays[day])} ·{' '}
          {formatTime(settings.cutoffTimes[day])}
        </span>
      );
    }
    return <span className="text-default-400">No cutoff</span>;
  };

  return (
    <Card className="border border-default-200">
      <CardBody className="p-6">
        {isUsingDefaults && (
          <div className="mb-5 flex items-start gap-2 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3">
            <FiInfo className="mt-0.5 h-4 w-4 shrink-0 text-warning-600" />
            <p className="text-sm text-warning-700">
              This distributor has no delivery settings for this chat — showing
              company defaults. Click Edit to set custom days & cutoff times.
            </p>
          </div>
        )}

        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center shrink-0">
              <FiClock className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-foreground">
                Delivery Days & Cutoff Times
              </h3>
              <p className="text-sm text-default-500">
                Customer specific delivery days and cutoff times for this chat.
              </p>
            </div>
          </div>
          <Button
            type="button"
            size="sm"
            variant="flat"
            color="primary"
            startContent={<FiEdit2 className="w-4 h-4" />}
            onPress={() => setIsModalOpen(true)}
          >
            Edit
          </Button>
        </div>

        <div
          className={`mt-5 space-y-3 ${
            isUsingDefaults ? 'opacity-50 pointer-events-none select-none' : ''
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium uppercase tracking-wide text-default-400">
              Cutoff type
            </span>
            <Chip size="sm" variant="flat" color="default">
              {cutoffTypeLabel}
            </Chip>
          </div>

          <div className="rounded-lg border border-default-200 divide-y divide-default-200">
            {DAYS.map((day) => {
              const isDeliveryDay = settings.deliveryDays[day];
              return (
                <div
                  key={day}
                  className={`flex items-center justify-between px-4 py-2.5 text-sm ${
                    isDeliveryDay ? '' : 'opacity-50'
                  }`}
                >
                  <span className="font-medium text-foreground">{day}</span>
                  {renderCutoffCell(day)}
                </div>
              );
            })}
          </div>
        </div>
      </CardBody>

      <DeliverySettingsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        chatId={chatId}
      />
    </Card>
  );
};
